import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Share2, Copy, Check, Share } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.tour.trigo";
const APP_STORE_URL = "https://tour.trigoride.com";

/**
 * Iter 80 — share dialog with a trackable short link.
 *
 * When the current user has a referral code, every outbound CTA goes
 * through `/api/s/{code}?platform=…` so the backend can log impressions
 * (Android / iOS / web) and credit the ambassador. When no code is
 * available (e.g. during registration), we fall back to the direct
 * store URLs.
 */
export default function ShareAppDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const code = (user?.referral_code || "").toUpperCase();
  const [copied, setCopied] = useState(false);

  // The short link we expose to the user — points at the backend router
  // that owns the UA sniffing + impression logging. We derive the base
  // from REACT_APP_BACKEND_URL so the link remains valid in every env.
  const shortLink = useMemo(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return code ? `${base}/api/s/${code}` : "";
  }, [code]);

  const platformHref = (plat) => {
    if (code && shortLink) return `${shortLink}?platform=${plat}`;
    return plat === "android" ? PLAY_STORE_URL : APP_STORE_URL;
  };

  const copyLink = async () => {
    if (!shortLink) return;
    try {
      await navigator.clipboard.writeText(shortLink);
      setCopied(true);
      toast.success("Lien copié dans le presse-papier");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share || !shortLink) { copyLink(); return; }
    try {
      await navigator.share({
        title: "TrigoTour",
        text: "Découvrez TrigoTour — covoiturage et location de véhicules avec chauffeur.",
        url: shortLink,
      });
    } catch { /* user cancelled */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="share-app-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
            <Share2 className="w-5 h-5 text-[#FACC15]" /> Télécharger TrigoTour
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            Partagez le bon lien avec vos amis — vous gagnez <strong className="text-[#0A2540]">150 TFT</strong> par inscription parrainée.
          </DialogDescription>
        </DialogHeader>

        {/* Short link with copy — only shown when the user has a code */}
        {shortLink && (
          <div className="mt-1 mb-2 bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl p-3" data-testid="share-short-link">
            <p className="text-[10px] uppercase font-bold tracking-widest text-[#64748B] mb-1">Votre lien court</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 text-xs sm:text-sm font-mono text-[#0A2540] truncate">{shortLink}</code>
              <button
                onClick={copyLink}
                className="shrink-0 h-9 w-9 rounded-lg bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] flex items-center justify-center"
                data-testid="share-copy-link"
                title="Copier le lien"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
              <button
                onClick={nativeShare}
                className="shrink-0 h-9 w-9 rounded-lg bg-[#0A2540] text-white hover:bg-[#081b2e] flex items-center justify-center"
                data-testid="share-native"
                title="Partager via l'OS"
              >
                <Share className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-[#64748B] mt-1.5">
              Chaque clic est tracé, chaque inscription vous rapporte 150 TFT.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 mt-1">
          <a
            href={platformHref("android")}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 rounded-xl bg-[#0A2540] text-white hover:bg-[#081b2e] transition"
            data-testid="share-dialog-android"
          >
            <svg viewBox="0 0 48 48" width="36" height="36" aria-hidden="true">
              <path fill="#4CAF50" d="M7.1 6.3C6.4 7 6 8 6 9.3v29.5c0 1.2.4 2.3 1.1 3L23 25 7.1 6.3z" />
              <path fill="#FFC107" d="M33 18.4L8.2 5.7c-.4-.2-.9-.3-1.3-.3L23 25l10-6.6z" />
              <path fill="#F44336" d="M33 31.6L23 25 6.9 44.3c.4 0 .8-.1 1.3-.3L33 31.6z" />
              <path fill="#2196F3" d="M41.4 22.1L34.8 18 25 24.5 33 31.6l8.4-4.2c1.3-.7 2-1.9 2-3.1 0-1.2-.7-2.4-2-2.2z" />
            </svg>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest opacity-70">Télécharger sur</p>
              <p className="text-base font-bold leading-tight" style={{ fontFamily: "Outfit" }}>Google Play</p>
              <p className="text-[11px] opacity-70">Android</p>
            </div>
          </a>

          <a
            href={platformHref("ios")}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 rounded-xl bg-[#0A2540] text-white hover:bg-[#081b2e] transition"
            data-testid="share-dialog-ios"
          >
            <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor" aria-hidden="true">
              <path d="M17.564 12.645c-.02-2.077 1.695-3.082 1.772-3.131-.967-1.414-2.475-1.608-3.013-1.632-1.283-.13-2.504.754-3.154.754-.65 0-1.656-.734-2.72-.714-1.399.02-2.687.812-3.408 2.063-1.453 2.516-.372 6.246 1.044 8.293.69 1.001 1.513 2.129 2.595 2.088 1.04-.042 1.436-.676 2.694-.676 1.26 0 1.613.676 2.72.654 1.123-.02 1.835-1.021 2.52-2.025.796-1.165 1.123-2.293 1.143-2.352-.026-.013-2.19-.839-2.213-3.322zm-2.15-6.104c.567-.69.95-1.642.846-2.597-.819.034-1.814.546-2.4 1.236-.526.612-.986 1.591-.862 2.52.914.07 1.85-.465 2.416-1.159z"/>
            </svg>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest opacity-70">Télécharger sur</p>
              <p className="text-base font-bold leading-tight" style={{ fontFamily: "Outfit" }}>App Store</p>
              <p className="text-[11px] opacity-70">iOS</p>
            </div>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
